<script lang="ts">
  import type { FormField } from '../types';
  import { resetField } from '$lib/helpers/form-field-reset';

  export let field: FormField;
  export let selectedValue: string;
  export let isError: boolean = false;

  let selectDropdown: HTMLDivElement;
  let displayInput: HTMLInputElement;
  let isOpen: boolean = false;
  let displayValue: string;

  const selectValueMap: { [key: string]: string } = {};

  field.selectOptions.forEach((option, index) => {
    if (option.value) {
      selectValueMap[option.value] = `${field.id}-${index}`;
    }
    if (option.selected && option.value) {
      selectedValue = option.value;
    }
  });

  function toggleDropdown() {
    isOpen ? closeDropdown() : openDropdown();
  }

  function openDropdown() {
    // close any open select since stopPropagation is applied to toggleDropdown
    // keeping only 1 close dropdown event on body click is open
    document.body.dispatchEvent(new Event('click'));
    isOpen = true;
    document.body.addEventListener('click', closeDropdown);
  }

  function closeDropdown() {
    isOpen = false;
    document.body.removeEventListener('click', closeDropdown);
  }

  function setAriaChecked(ariaValue: string) {
    const element = selectDropdown.querySelector<HTMLElement>(`#selectselectPrefixFor${selectValueMap[selectedValue]}`);
    if (element) {
      element.ariaChecked = ariaValue;
    }
  }

  function setValue(value: string | undefined, label: string) {
    if (value) {
      setAriaChecked('false');
      displayValue = label;
      selectedValue = value;
      setAriaChecked('true');
    }
  }

  /* eslint-disable no-fallthrough, default-case */
  function handleInputTriggerKeydown(e: KeyboardEvent & { currentTarget: EventTarget & HTMLInputElement }) {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case ' ': {
        e.preventDefault();
        if (selectedValue) {
          selectDropdown.querySelector<HTMLElement>(`#selectPrefixFor${selectValueMap[selectedValue]}`)?.focus();
        } else {
          const firstElement = selectValueMap[field.selectOptions[1].value];
          selectDropdown.querySelector<HTMLElement>(`#selectPrefixFor${firstElement}`)?.focus();
        }
        openDropdown();
        break;
      }
      case 'Escape': {
        e.preventDefault();
      }
      case 'Tab': {
        closeDropdown();
      }
    }
  }

  function handleDropdownKeydown(e: KeyboardEvent & { currentTarget: EventTarget & HTMLDivElement }): any {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        (document.activeElement?.nextElementSibling as HTMLElement)?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        (document.activeElement?.previousElementSibling as HTMLElement)?.focus();
        break;
      }
      case 'Enter':
      case ' ': {
        e?.target?.dispatchEvent(new Event('click'));
      }
      case 'Escape': {
        e.preventDefault();
        displayInput.focus();
      }
      case 'Tab': {
        closeDropdown();
        break;
      }
    }
  }
  /* eslint-enable no-fallthrough, default-case */
</script>

<div class:isError class="marketInput marketSelect" on:click|stopPropagation={toggleDropdown} aria-expanded={isOpen}>
  <input
    type="text"
    bind:this={displayInput}
    bind:value={displayValue}
    placeholder={field.placeholder || ' '}
    id={field.id}
    autocomplete={field.autocompleteType}
    required={field.required}
    use:resetField
    readonly={true}
    on:keydown={handleInputTriggerKeydown}
  />
  {#if field.label}
    <label class="fieldLabel">{field.label}</label>
  {/if}
  <span class="arrowIcon">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14">
      <path d="M1 5l6 6 6-6" fill="none" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  </span>

  <div
    class="selectDropdown"
    role="radiogroup"
    on:keydown={handleDropdownKeydown}
    bind:this={selectDropdown}
    tabindex="0"
  >
    <div class="selectScroll">
      {#each field.selectOptions as selectOption}
        <!-- this is fine because the outer div handles using the keyboard to navigate dropdown -->
        <!-- remove default Select-->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        {#if selectOption.label !== 'Select...'}
          <div
            class="selectOption"
            id="selectPrefixFor{selectValueMap[selectOption.value]}"
            data-label={selectOption.label}
            role="radio"
            tabindex="-1"
            aria-checked={selectValueMap[selectOption.value] === selectedValue}
            on:click={() => setValue(selectOption.value, selectOption.label)}
          >
            <label
              class="selectOptionLabel"
              for="selectPrefixFor{selectValueMap[selectOption.value]}"
              id="labelFor{selectValueMap[selectOption.value]}"
            >
              {selectOption.label}
            </label>
          </div>
        {/if}
      {/each}
    </div>
  </div>

  <!--  hidden-->
  <input
    bind:value={selectedValue}
    title={field.validationMessage}
    required={field.required}
    use:resetField
    class="fs-exclude setValue"
    type="hidden"
    id={field.id}
    name={field.id}
  />
</div>

<style lang="scss">
  .marketInput {
    min-height: var(--field-input-height);
    flex-flow: column-reverse;
    cursor: pointer;

    .selectDropdown {
      z-index: 200;
      position: absolute;
      top: calc(100% - -7px);
      width: 100%;
      left: 0;
      background-color: white;
      opacity: 0;
      visibility: hidden;
      overflow: hidden;
      border-radius: 6px;
      transition:
        opacity 0.3s cubic-bezier(0, 0, 0.2, 1),
        visibility 0.3s cubic-bezier(0, 0, 0.2, 1);
      background-color: rgba(255, 255, 255, 1);
      box-shadow:
        rgba(0, 0, 0, 0.08) 0px 2px 16px,
        rgba(0, 0, 0, 0.08) 0px 4px 8px;

      &::after {
        content: '';
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 0 0 5px 5px;
      }
    }

    .selectScroll {
      overflow-y: auto;
      max-height: 300px;
      padding: 12px;
    }

    .selectOption {
      padding: 12px;
      height: 56px;
      display: flex;
      align-items: center;
      border-radius: 6px;
      cursor: pointer;

      &:focus,
      &:hover {
        outline: none;
        background-color: #e6f0ff;
      }

      .selectOptionLabel {
        font-weight: var(--font-weight-medium);
        color: var(--field-focus-state-float-phase-label-color);
        letter-spacing: 0;
        transform-origin: 0px;
        font-size: var(--field-input-size);
        transform: none;
        cursor: pointer;
      }

      &[aria-checked='true'] {
        background-color: #e6f0ff;
        .selectOptionLabel {
          color: #005ad9;
        }
      }
    }

    .fieldLabel {
      bottom: 4px;
      position: relative;
      cursor: pointer;
      padding-right: var(--space-10);
    }

    .arrowIcon {
      flex-shrink: 0;
      width: 14px;
      line-height: 1;
      display: block;
      stroke: var(--color-gray-55);
      position: absolute;
      right: 16px;
      bottom: calc(50% - 8px);
    }

    input {
      cursor: pointer;
    }

    &[aria-expanded='true'] {
      .selectDropdown {
        opacity: 1;
        visibility: visible;
      }

      .arrowIcon {
        transform: rotateZ(180deg);
      }

      .fieldLabel {
        font-weight: var(--field-float-phase-label-text-weight);
        transform: scale(
          calc(var(--field-float-phase-label-text-size-unitless) / var(--field-empty-phase-label-text-size-unitless))
        );
        color: var(--field-focus-state-float-phase-label-color);
      }
    }

    &[aria-expanded='false'] {
      input:placeholder-shown + label {
        color: rgba(0, 0, 0, 0.55);
        transform: translateY(var(--field-input-label-translate));
      }
    }
  }
</style>
